/**
 * Eigenbau: Auto Q&A — Lauf im Einzelnen in der Form der Vorlage.
 *
 * Die Vorlage baut die Seite aus fünf Teilen: der Zurück-Link, der Kopf mit
 * Lauf-Nummer, Zustand und Ziel, der Titel in 40, das Zahlenband über der
 * Tintenlinie (Befunde als Leitzahl, daneben die Schwere-Zählung), dann die
 * Kette als Zeitstrahl, das Schritt-Detail mit Abdeckung und Vorschrift und
 * die Befunde — rechts die schwarze Tafel mit den Lauf-Daten.
 *
 * Der Titel ist der Rezeptname, niemals `lauf.prompt`. Die Vorschrift gehört
 * in den Tafelblock darunter. Der Lauf kommt über `durchsicht.lauf`.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCursorListe, useProjekte } from "@/lib/haken";
import { MehrLaden } from "@/components/MehrLaden";
import { Titel } from "@/components/entwurf/Titel";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { SCHWERE, STAND } from "@/lib/entwurf-zustaende";
import { laufAnzeigetitel } from "@/lib/auto-qa-anzeige";
import { cx } from "@/lib/utils";

type Befund = {
  id: string;
  datei: string;
  zeile: number | null;
  schwere: string;
  text: string;
  zustand: string;
  geaendertAm: string | Date;
};

const SCHWERE_ORDNUNG = [
  "kritisch",
  "schwer",
  "mittel",
  "gering",
  "hinweis",
] as const;

type BefundSchwere = (typeof SCHWERE_ORDNUNG)[number];

const SCHWERE_LABEL: Readonly<Record<BefundSchwere, string>> = {
  kritisch: "Kritisch",
  schwer: "Schwer",
  mittel: "Mittel",
  gering: "Gering",
  hinweis: "Hinweis",
};

const BEFUND_ZUSTAND_LABEL: Record<string, string> = {
  offen: "Offen",
  bestaetigt: "Bestätigt",
  verworfen: "Verworfen",
  behoben: "Nachweislich behoben",
};

function vorZeit(wert: string | Date | null | undefined): string {
  if (!wert) return "—";
  const minuten = Math.max(
    0,
    Math.floor((Date.now() - new Date(wert).getTime()) / 60_000),
  );
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  return `vor ${Math.floor(stunden / 24)} Tagen`;
}

function schwereBild(schwere: string) {
  if (schwere === "kritisch" || schwere === "schwer") return SCHWERE.hoch;
  if (schwere === "mittel") return SCHWERE.mittel;
  if (schwere === "gering") return SCHWERE.niedrig;
  return SCHWERE.hinweis;
}

export function AutoQaLaufDetail() {
  const { laufId } = useParams({ from: "/_app/auto-qa/laeufe/$laufId" });
  const projekte = useProjekte();
  const client = useQueryClient();
  const [befundFehler, setzeBefundFehler] = useState<string | null>(null);
  const [vorschriftKopiert, setzeVorschriftKopiert] = useState(false);

  const laufQuery = useQuery({
    queryKey: ["cubic", "durchsicht", "lauf", laufId],
    queryFn: () => api.durchsicht.lauf.query({ laufId }),
    enabled: Boolean(laufId),
  });

  const befundeQuery = useCursorListe({
    queryKey: ["cubic", "durchsicht", "befunde", laufId],
    lade: (cursor) =>
      api.durchsicht.befunde.query({ laufId, cursor, grenze: 50 }),
    enabled: Boolean(laufId),
  });

  const befundAendern = useMutation({
    mutationFn: (eingabe: {
      befundId: string;
      schwere?: BefundSchwere;
      wiedereroeffnen?: boolean;
      erwarteteRevision: Date;
    }) => api.durchsicht.befundAendern.mutate(eingabe),
    onSuccess: async () => {
      setzeBefundFehler(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "durchsicht", "befunde", laufId],
      });
    },
    onError: (fehler) => {
      setzeBefundFehler(
        fehler instanceof Error
          ? fehler.message
          : "Der Befund konnte nicht geändert werden.",
      );
    },
  });

  const befundFreigeben = useMutation({
    mutationFn: (eingabe: {
      befundId: string;
      art: "uebernehmen" | "ablehnen" | "zurueckstellen";
    }) => api.durchsicht.freigeben.mutate(eingabe),
    onSuccess: async () => {
      setzeBefundFehler(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "durchsicht", "befunde", laufId],
      });
    },
    onError: (fehler) => {
      setzeBefundFehler(
        fehler instanceof Error
          ? fehler.message
          : "Die Befundentscheidung konnte nicht gespeichert werden.",
      );
    },
  });

  const lauf = laufQuery.data;
  const befunde = befundeQuery.eintraege as Befund[];

  const schwereZaehlung = useMemo(() => {
    const zaehlung = new Map<string, number>();
    for (const b of befunde) {
      zaehlung.set(b.schwere, (zaehlung.get(b.schwere) ?? 0) + 1);
    }
    return [...SCHWERE_ORDNUNG]
      .map((schwere) => ({
        schwere,
        wert: zaehlung.get(schwere) ?? 0,
      }))
      .filter((z) => z.wert > 0);
  }, [befunde]);

  const ausgelassen = (lauf?.abdeckung ?? []) as unknown as
    | Array<{ pfad: string; grund: string }>
    | undefined;

  const projekteName = lauf?.projektId
    ? projekte.data?.find((p) => p.id === lauf.projektId)?.name
    : null;

  if (laufQuery.isLoading) {
    return (
      <div className="px-9 py-14">
        <Mikrolabel grad={11.5}>Lade Lauf …</Mikrolabel>
      </div>
    );
  }

  if (laufQuery.isError || !lauf) {
    return (
      <div className="px-9 py-14">
        <Mikrolabel grad={11.5}>
          Lauf nicht gefunden oder nicht sichtbar.
        </Mikrolabel>
      </div>
    );
  }

  const stand = STAND[lauf.zustand as keyof typeof STAND] ?? STAND.wartet;
  const dauer =
    lauf.dauerMs != null ? `${(lauf.dauerMs / 1000).toFixed(1)} s` : "—";
  const titel = laufAnzeigetitel(lauf);
  const vorschrift = lauf.prompt ?? "";

  async function kopiereVorschrift(): Promise<void> {
    if (!vorschrift) return;
    try {
      await navigator.clipboard.writeText(vorschrift);
      setzeVorschriftKopiert(true);
    } catch {
      setzeVorschriftKopiert(false);
    }
  }

  return (
    <div className="flex min-h-full items-stretch">
      <div className="min-w-[300px] flex-1 px-9 pb-24 pt-14">
        <div className="max-w-[720px]">
          <Link
            to="/auto-qa"
            className="inline-flex items-center gap-2 font-mono text-[12px] text-akzent underline decoration-1 underline-offset-[3px]"
          >
            <span aria-hidden>←</span> Auto Q&amp;A
          </Link>

          <div className="mt-[22px] flex flex-wrap items-center gap-3.5">
            <Mikrolabel grad={12.5} ton="leise">
              Lauf {lauf.id.slice(0, 4)}
            </Mikrolabel>
            <span className="h-[13px] w-px bg-linie3" />
            <span className="flex items-center gap-2">
              <span
                className={cx(
                  "size-[7px] flex-none",
                  stand.fuellung,
                  stand.strich ? "bg-transparent" : "",
                )}
              />
              <Mikrolabel grad={12} ton="laut">
                {stand.label}
              </Mikrolabel>
            </span>
            {projekteName ? (
              <>
                <span className="h-[13px] w-px bg-linie3" />
                <Mikrolabel grad={12} ton="leise">
                  {projekteName}
                </Mikrolabel>
              </>
            ) : null}
          </div>

          <Titel grad={40} className="mt-[18px]">
            {titel}
          </Titel>
          {lauf.letzterFehler ? (
            <div className="mt-5 border-l-2 border-warn bg-warnflaeche px-3.5 py-2.5">
              <Mikrolabel grad={12}>{lauf.letzterFehler}</Mikrolabel>
            </div>
          ) : null}
          <p className="mt-5 max-w-[60ch] text-[15.5px] font-light leading-[1.7] text-tinte2">
            Ausgelöst am {new Date(lauf.angelegtAm).toLocaleString("de-DE")} ·
            Rezeptfassung{" "}
            {lauf.rezeptFassungId ? lauf.rezeptFassungId.slice(0, 8) : "—"}
            {lauf.versuche > 0 ? ` · ${lauf.versuche} Versuch(e)` : ""}.
          </p>

          <Zahlenband className="mt-11">
            <Kennzahl
              label="Befunde"
              grad={56}
              wert={befunde.length}
              ton={
                befunde.some((b) => b.schwere === "kritisch")
                  ? "akzent"
                  : undefined
              }
            />
            <div className="flex min-w-[240px] flex-1 flex-wrap gap-x-5 gap-y-2 pb-2">
              {schwereZaehlung.map((z) => (
                <span
                  key={z.schwere}
                  className="flex items-center gap-[7px] text-tinte3"
                >
                  <span
                    className={cx(
                      "size-[7px] flex-none",
                      schwereBild(z.schwere).fuellung,
                      "shadow-[inset_0_0_0_1px_var(--linie3)]",
                    )}
                  />
                  <span className="tabular-nums text-tinte">
                    {z.wert}
                  </span>{" "}
                  {SCHWERE_LABEL[z.schwere]}
                </span>
              ))}
            </div>
          </Zahlenband>

          <div className="mt-14">
            <Rubrik anzahl={lauf.modell || "Modell"}>Kette</Rubrik>
            <div className="flex items-start border-b border-linie">
              <span className="w-[34px] flex-none py-[18px] font-mono text-[11.5px] tabular-nums text-tinte4">
                01
              </span>
              <span className="relative w-[26px] flex-none self-stretch">
                <span className="absolute bottom-0 left-3 top-0 w-px bg-linie2" />
                <span
                  className={cx(
                    "absolute left-[8px] top-[21px] size-[9px]",
                    stand.fuellung,
                    "shadow-[inset_0_0_0_1px_var(--linie2)]",
                  )}
                />
              </span>
              <span className="min-w-0 flex-1 py-[18px] pl-[18px]">
                <span className="block text-[14.5px] text-tinte">
                  {titel}
                </span>
                <span className="mt-1 block font-mono text-[11.5px] text-tinte4">
                  {lauf.modell || "Modell"} · {dauer}
                </span>
              </span>
              <span className="w-[92px] flex-none py-[18px] text-right font-mono text-[11.5px] text-tinte4">
                {stand.label}
              </span>
              <span className="w-[74px] flex-none py-[18px] text-right font-mono text-[11.5px] tabular-nums text-tinte4">
                {dauer}
              </span>
            </div>
          </div>

          {ausgelassen && ausgelassen.length > 0 ? (
            <div className="py-[18px]">
              <Rubrik anzahl={`${ausgelassen.length} nicht geprüft`}>
                Ausgelassen
              </Rubrik>
              {ausgelassen.map((a) => (
                <div
                  key={a.pfad}
                  className="mt-2 font-mono text-[11.5px] text-tinte4"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {a.pfad} · {a.grund}
                </div>
              ))}
            </div>
          ) : null}

          <div className="mt-14">
            <Rubrik
              aktion={
                <button
                  type="button"
                  onClick={() => void kopiereVorschrift()}
                  disabled={!vorschrift}
                  className="font-mono text-[11.5px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-40"
                >
                  {vorschriftKopiert ? "Kopiert" : "Kopieren"}
                </button>
              }
            >
              Vorschrift
            </Rubrik>
            <div className="mt-3 overflow-x-auto bg-tafel px-[18px] py-4 font-mono text-[12.5px] leading-[1.75] text-tafel-tinte">
              <pre className="whitespace-pre-wrap">{vorschrift || "—"}</pre>
            </div>
          </div>

          <div className="mt-14">
            <Rubrik anzahl={`${befunde.length} nach Schwere sortiert`}>
              Befunde
            </Rubrik>
            {befundFehler ? (
              <p
                role="alert"
                className="border-b border-linie py-3 font-mono text-[11.5px] text-warn"
              >
                {befundFehler}
              </p>
            ) : null}
            {befunde.length === 0 ? (
              <div className="border-b border-linie py-6">
                <Mikrolabel grad={11.5}>
                  Keine Befunde in diesem Lauf.
                </Mikrolabel>
              </div>
            ) : (
              befunde.map((b) => {
                const bild = schwereBild(b.schwere);
                return (
                  <div key={b.id} className="border-b border-linie py-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        className={cx(
                          "size-2 flex-none",
                          bild.fuellung,
                          "shadow-[inset_0_0_0_1px_var(--linie3)]",
                        )}
                      />
                      <span
                        className={cx(
                          "font-mono text-[11.5px] font-medium",
                          bild.text,
                        )}
                      >
                        {SCHWERE_LABEL[b.schwere as BefundSchwere] ?? b.schwere}
                      </span>
                      <span className="font-mono text-[11.5px] text-tinte">
                        {BEFUND_ZUSTAND_LABEL[b.zustand] ?? b.zustand}
                      </span>
                      <span className="ml-auto font-mono text-[11.5px] text-tinte4">
                        {titel}
                      </span>
                    </div>
                    <div className="mt-2.5 text-[15px] leading-[1.4] text-tinte">
                      {b.text}
                    </div>
                    <div className="mt-1.5 font-mono text-[12px] text-akzent" style={{ overflowWrap: "anywhere" }}>
                      {b.datei}
                      {b.zeile != null ? `:${b.zeile}` : ""}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <label className="font-mono text-[11px] text-tinte4">
                        Schwere{" "}
                        <select
                          value={b.schwere}
                          aria-label={`Schwere von ${b.datei}${b.zeile != null ? `:${b.zeile}` : ""}`}
                          className="ml-1 border border-linie bg-transparent px-2 py-1 text-tinte outline-none focus:border-akzent"
                          disabled={befundAendern.isPending || befundFreigeben.isPending}
                          onChange={(ereignis) =>
                            befundAendern.mutate({
                              befundId: b.id,
                              schwere: ereignis.target.value as BefundSchwere,
                              erwarteteRevision: new Date(b.geaendertAm),
                            })
                          }
                        >
                          {SCHWERE_ORDNUNG.map((schwere) => (
                            <option key={schwere} value={schwere}>
                              {SCHWERE_LABEL[schwere]}
                            </option>
                          ))}
                        </select>
                      </label>
                      {b.zustand === "offen" ? (
                        <>
                          <button
                            type="button"
                            className="font-mono text-[11px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-40"
                            disabled={befundAendern.isPending || befundFreigeben.isPending}
                            onClick={() =>
                              befundFreigeben.mutate({
                                befundId: b.id,
                                art: "uebernehmen",
                              })
                            }
                          >
                            Übernehmen
                          </button>
                          <button
                            type="button"
                            className="font-mono text-[11px] text-tinte3 underline decoration-1 underline-offset-[3px] disabled:opacity-40"
                            disabled={befundAendern.isPending || befundFreigeben.isPending}
                            onClick={() =>
                              befundFreigeben.mutate({
                                befundId: b.id,
                                art: "zurueckstellen",
                              })
                            }
                          >
                            Zurückstellen
                          </button>
                          <button
                            type="button"
                            className="font-mono text-[11px] text-tinte4 underline decoration-1 underline-offset-[3px] disabled:opacity-40"
                            disabled={befundAendern.isPending || befundFreigeben.isPending}
                            onClick={() =>
                              befundFreigeben.mutate({
                                befundId: b.id,
                                art: "ablehnen",
                              })
                            }
                          >
                            Ablehnen
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="font-mono text-[11px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-40"
                          disabled={befundAendern.isPending || befundFreigeben.isPending}
                          onClick={() =>
                            befundAendern.mutate({
                              befundId: b.id,
                              wiedereroeffnen: true,
                              erwarteteRevision: new Date(b.geaendertAm),
                            })
                          }
                        >
                          Wiedereröffnen
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <MehrLaden
              hatMehr={befundeQuery.hasNextPage}
              laedt={befundeQuery.isFetchingNextPage}
              laden={() => void befundeQuery.fetchNextPage()}
              beschriftung="Weitere Befunde laden"
            />
          </div>
        </div>
      </div>

      <div className="min-w-[248px] flex-[0_1_300px] bg-tafel px-[26px] pb-24 pt-14">
        <div className="border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Lauf
        </div>
        {[
          ["Nummer", `Lauf ${lauf.id.slice(0, 4)}`],
          ["Zustand", stand.label],
          ["Modell", lauf.modell || "—"],
          ["Dauer", dauer],
          ["Angelegt", vorZeit(lauf.angelegtAm)],
          ["Projekt", projekteName ?? "—"],
          ["Versuche", String(lauf.versuche)],
          ["Gekürzt", lauf.gekuerzt > 0 ? `${lauf.gekuerzt} Stellen` : "nein"],
        ].map(([label, wert]) => (
          <div
            key={label}
            className="flex items-baseline gap-3 border-b border-tafel-wisch py-3"
          >
            <span className="w-[96px] flex-none font-mono text-[11px] text-tafel-tinte3">
              {label}
            </span>
            <span
              className="min-w-0 flex-1 text-[13px] font-light text-tafel-tinte"
              style={{ wordBreak: "break-word" }}
            >
              {wert}
            </span>
          </div>
        ))}

        <div className="mt-10 border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Rezeptfassung
        </div>
        <p className="mt-3.5 text-[12.5px] font-light leading-[1.6] text-tafel-tinte3">
          {lauf.rezeptFassungId
            ? `Fassung ${lauf.rezeptFassungId.slice(0, 8)}`
            : "Keine Fassung hinterlegt."}
        </p>
      </div>
    </div>
  );
}
